import { Add, Delete } from "@mui/icons-material";
import { Box, Button, IconButton, List, Stack, Typography } from "@mui/material";
import { TFunction } from "i18next";
import { times } from "lodash";
import { useMemo } from "react";
import { Control, useFieldArray } from "react-hook-form";
import { SelectElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { AutocompleteModel, GqlPermissions } from "~/__generated__";
import useAutocompleteGetOptions from "~/modules/app/api/useAutocompleteGetOptions";
import GaiaSliderElement from "~/modules/app/components/GaiaSliderElement";
import SliderTextInput from "~/modules/app/components/GaiaSliderElement/SliderTextInput";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import { ProtocolVisitRelativeTo, ProtocolVisitTimeslot } from "../../types/ProtocolVisitTimeslot";
import { ProtocolVisitFormInput } from "../ProtocolVisitsEditor/VisitEditForm";

// Creates array for the timeoffset options -12 to 12 with intervals of 0.5
const timeOptions = times(49, i => {
    const id = -12 + i * 0.5;
    return { id, label: `${id > 0 ? "+" : ""}${id}` };
});

const defaultTimeslot: ProtocolVisitTimeslot = {
    id: "",
    startRelativeTo: ProtocolVisitRelativeTo.Sunset,
    maxStartOffset: 0,
    endRelativeTo: ProtocolVisitRelativeTo.Sunset,
    minEndOffset: 2,
    availabilityId: "",
};

const relativeToOptions = (t: TFunction<"protocol", undefined>) => [
    {
        id: ProtocolVisitRelativeTo.Sunset,
        label: t("timeslotsRelativeTo.relativeToSunset"),
    },
    {
        id: ProtocolVisitRelativeTo.Sunrise,
        label: t("timeslotsRelativeTo.relativeToSunrise"),
    },
    {
        id: ProtocolVisitRelativeTo.Midnight,
        label: t("timeslotsRelativeTo.relativeToMidnight"),
    },
];

type Props = {
    control: Control<ProtocolVisitFormInput, unknown>;
};

export default function ProtocolVistsTimeslotsEditor({ control }: Props) {
    const { t } = useTranslation("protocol");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const relativeToOps = useMemo(() => relativeToOptions(t), [t]);
    const { fields, append, remove } = useFieldArray({
        control,
        name: "timeslots",
    });

    const { data: availabilitiesList } = useAutocompleteGetOptions({
        model: AutocompleteModel.Availability,
    });

    return (
        <Stack>
            <Box paddingBottom={4}>
                <GaiaSliderElement
                    disabled={!isAdmin}
                    control={control}
                    label={t("protocolVistsTimeslotsEditor.labelDuration")}
                    name="minDurationInHours"
                    step={0.5}
                    min={0.5}
                    max={5}
                    inputElement={({ min, max, step }) => (
                        <SliderTextInput
                            control={control}
                            name="minDurationInHours"
                            disabled={!isAdmin}
                            suffix={t("protocolVistsTimeslotsEditor.durationSuffix")}
                            min={min}
                            max={max}
                            step={step}
                        />
                    )}
                />
                <Typography variant="caption">{t("protocolVistsTimeslotsEditor.durationDescription")}</Typography>
            </Box>

            <Stack component={List} spacing={2}>
                {fields.map((field, index) => (
                    <Box key={field.id}>
                        <Stack key={field.id} direction="row" alignItems="center" spacing={2} component="li">
                            <Typography variant="h4">#{index}</Typography>
                            <Stack flex={1} spacing={1}>
                                <Stack direction="row" spacing={1} minWidth={1} alignItems="center">
                                    <SelectElement
                                        sx={{ flex: 1 }}
                                        disabled={!isAdmin}
                                        control={control}
                                        label={t("protocolVistsTimeslotsEditor.labelStartRelativeTo")}
                                        name={`timeslots.${index}.startRelativeTo`}
                                        options={relativeToOps}
                                    />
                                    <SelectElement
                                        sx={{ flex: 1 }}
                                        disabled={!isAdmin}
                                        control={control}
                                        name={`timeslots.${index}.maxStartOffset`}
                                        label={t("protocolVistsTimeslotsEditor.labelStartOffset")}
                                        options={timeOptions}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={1} minWidth={1} alignItems="center">
                                    <SelectElement
                                        sx={{ flex: 1 }}
                                        disabled={!isAdmin}
                                        control={control}
                                        label={t("protocolVistsTimeslotsEditor.labelEndRelativeTo")}
                                        name={`timeslots.${index}.endRelativeTo`}
                                        options={relativeToOps}
                                    />
                                    <SelectElement
                                        sx={{ flex: 1 }}
                                        disabled={!isAdmin}
                                        control={control}
                                        name={`timeslots.${index}.minEndOffset`}
                                        label={t("protocolVistsTimeslotsEditor.labelEndOffset")}
                                        options={timeOptions}
                                    />
                                </Stack>
                            </Stack>
                            {isAdmin && (
                                <IconButton
                                    onClick={() => {
                                        remove(index);
                                    }}
                                >
                                    <Delete />
                                </IconButton>
                            )}
                        </Stack>
                        {availabilitiesList ? (
                            <SelectElement
                                sx={{ marginTop: 2, width: "100%" }}
                                disabled={!isAdmin}
                                control={control}
                                label={t("protocolVistsTimeslotsEditor.availability")}
                                rules={{
                                    required: t("protocolVistsTimeslotsEditor.availabilityRequired"),
                                }}
                                name={`timeslots.${index}.availabilityId`}
                                valueKey="value"
                                options={[...(availabilitiesList || [])]}
                            />
                        ) : null}
                    </Box>
                ))}
            </Stack>
            {isAdmin && (
                <Button
                    startIcon={<Add />}
                    onClick={() => {
                        append({ ...defaultTimeslot, id: uuid() });
                    }}
                >
                    {t("protocolVistsTimeslotsEditor.addTimeslot")}
                </Button>
            )}
        </Stack>
    );
}
